import moment from "moment";
import { useEffect, useRef } from "react";
import type {
  FoaPart,
  ProcedureType,
  Regulation,
  SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { minTenderDays } from "../utils/karens";
import { daysBetween } from "../utils/milestoneOffsets";
import { isDpsCompetition, isDpsScheme, isTwoStageProcedure } from "../utils/procedure";

// Margin over the statutory floor, so moving the announcement by a day does not invalidate it.
const MARGIN_DAYS = 7;

interface SuggestedBidDeadline {
  regulation: Regulation | null;
  foaPart: FoaPart | null;
  procedureType: ProcedureType | null;
  announcementDate: string | null;
  bidSubmissionDeadline: string | null;
  isSaving: boolean;
  updateField: (fields: Partial<SourcingEventV2PatchDto>) => void;
}

export const useSuggestedBidDeadline = ({
  regulation,
  foaPart,
  procedureType,
  announcementDate,
  bidSubmissionDeadline,
  isSaving,
  updateField,
}: SuggestedBidDeadline): void => {
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current) return;
    if (isSaving) return;
    // Two-stage moves the tender period, not the deadline itself, and a DPS purchase is never
    // announced (§ 26-7) so there is no announcement to count from.
    if (isTwoStageProcedure(procedureType)) return;
    if (isDpsScheme(procedureType) || isDpsCompetition(procedureType)) return;
    // Below the EEA tier no fixed floor applies, so there is no minimum to add margin to.
    const minDays = minTenderDays(regulation, foaPart, procedureType);
    if (minDays == null) return;
    if (announcementDate == null) return;
    // A deadline that already clears the floor is the buyer's to keep, however short the margin.
    if (
      bidSubmissionDeadline !== null &&
      daysBetween(announcementDate, bidSubmissionDeadline) >= minDays
    ) {
      return;
    }

    // The floor runs from the announcement in FOA § 20-3 but from now in the BFF's check, so the
    // later of the two is what satisfies both.
    const anchor = moment.max(moment(announcementDate), moment());
    seededRef.current = true;
    updateField({
      eventData: {
        bidSubmissionDeadline: anchor
          .add(minDays + MARGIN_DAYS, "days")
          .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
          .toISOString(),
      },
    });
  }, [
    regulation,
    foaPart,
    procedureType,
    announcementDate,
    bidSubmissionDeadline,
    isSaving,
    updateField,
  ]);
};
